import React, { useEffect, useState, Component } from 'react';
import { ChevronDown, FileText, Mail, Home } from 'lucide-react';
import { callZohoFlow } from './helper';
// Helper function to get URL parameter
const getUrlParam = (param: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param);
};
// Form name to view mapping
const FORM_NAME_MAP: Record<string, ActiveView> = {
  'GUIA DE ESTUDO': 'form1',
  'SITE OBESCOLHIDOS': 'form2'
};
// Reusable Input Component for Form 1
const InputField = ({
  label,
  required = false,
  type = 'text',
  placeholder = '',
  className = ''
}: {
  label: string;
  required?: boolean;
  type?: string;
  placeholder?: string;
  className?: string;
}) => <div className={`flex flex-col space-y-1 ${className}`} data-id="element-0">
    <label className="text-[#4A90E2] font-medium text-sm ml-1" data-id="element-1">
      {label} {required && <span className="text-red-500" data-id="element-2">*</span>}
    </label>
    <input type={type} className="w-full bg-[#F2F2F2] border-none rounded-full px-4 py-3 text-gray-700 focus:ring-2 focus:ring-[#4A90E2] focus:outline-none transition-all" placeholder={placeholder} data-id="element-3" />
  </div>;
// Hidden field component for Form 1
const HiddenField = ({
  label,
  value,
  isDropdown = false
}: {
  label: string;
  value: string;
  isDropdown?: boolean;
}) => <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 relative group" data-id="element-4">
    <div className="flex justify-between items-center mb-1" data-id="element-5">
      <label className="text-[#4A90E2] font-medium text-sm" data-id="element-6">{label}</label>
      <span className="text-[10px] text-gray-400 italic uppercase tracking-wider" data-id="element-7">
        Hidden
      </span>
    </div>
    <div className="bg-[#EFEFEF] rounded-md px-3 py-2 text-gray-700 text-sm font-medium flex justify-between items-center" data-id="element-8">
      {value}
      {isDropdown && <ChevronDown className="w-4 h-4 text-gray-500" data-id="element-9" />}
    </div>
  </div>;
// Input component for Form 2 (teal theme)
const TealInputField = ({
  label,
  required = false,
  type = 'text',
  placeholder = ''
}: {
  label: string;
  required?: boolean;
  type?: string;
  placeholder?: string;
}) => <div className="flex flex-col space-y-2" data-id="element-10">
    <label className="text-white font-medium text-sm" data-id="element-11">
      {label} {required && <span className="text-red-400" data-id="element-12">*</span>}
    </label>
    <input type={type} className="w-full bg-white border-none rounded px-4 py-3 text-gray-700 focus:ring-2 focus:ring-white focus:outline-none" placeholder={placeholder} data-id="element-13" />
  </div>;
// Form 1 Component
const Form1 = ({
  onSubmit
}: {
  onSubmit: () => void;
}) => {
  const [agreed, setAgreed] = useState(false);
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [dataNascimento, setDataNascimento] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Hidden field values (not displayed but sent on submit)
  const team = 'Brazil';
  const formName = 'GUIA DE ESTUDO';
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed) {
      alert('Por favor, aceite os termos para continuar.');
      return;
    }
    setIsSubmitting(true);
    try {
      // Split name into first and last name
      const nameParts = nome.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      await callZohoFlow({
        first_name: firstName,
        last_name: lastName,
        email: email,
        dob: dataNascimento,
        team: team,
        formname: formName
      });
      onSubmit();
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Erro ao enviar formulário. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="bg-white w-full max-w-[550px] rounded-2xl shadow-xl p-8 md:p-10" data-id="element-14">
      <p className="text-xs text-gray-500 mb-6 font-medium" data-id="element-15">
        Click here and add your text
      </p>

      <form className="space-y-6" onSubmit={handleSubmit} data-id="element-16">
        <div className="flex flex-col space-y-1" data-id="element-17">
          <label className="text-[#4A90E2] font-medium text-sm ml-1" data-id="element-18">
            Nome <span className="text-red-500" data-id="element-19">*</span>
          </label>
          <input type="text" value={nome} onChange={e => setNome(e.target.value)} required className="w-full bg-[#F2F2F2] border-none rounded-full px-4 py-3 text-gray-700 focus:ring-2 focus:ring-[#4A90E2] focus:outline-none transition-all" data-id="element-20" />
        </div>

        <div className="flex flex-col space-y-1" data-id="element-21">
          <label className="text-[#4A90E2] font-medium text-sm ml-1" data-id="element-22">
            Email <span className="text-red-500" data-id="element-23">*</span>
          </label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full bg-[#F2F2F2] border-none rounded-full px-4 py-3 text-gray-700 focus:ring-2 focus:ring-[#4A90E2] focus:outline-none transition-all" data-id="element-24" />
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:space-x-4" data-id="element-25">
          <label className="text-[#4A90E2] font-medium text-sm ml-1 md:w-1/3 mb-2 md:mb-0" data-id="element-26">
            Data de
            <br data-id="element-27" />
            Nascimento
          </label>
          <input type="date" value={dataNascimento} onChange={e => setDataNascimento(e.target.value)} className="w-full md:w-2/3 bg-[#F2F2F2] border-none rounded-full px-4 py-3 text-gray-700 focus:ring-2 focus:ring-[#4A90E2] focus:outline-none" data-id="element-28" />
        </div>

        <div className="text-red-500 text-xs font-medium pt-2" data-id="element-29">
          *Required Fields
        </div>

        <div className="flex items-start space-x-3 pt-2" data-id="element-30">
          <div className="flex items-center h-5" data-id="element-31">
            <input id="terms1" type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="h-4 w-4 text-[#1ABC9C] border-gray-300 rounded focus:ring-[#1ABC9C]" data-id="element-32" />
          </div>
          <label htmlFor="terms1" className="text-xs text-black leading-tight" data-id="element-33">
            Eu concordo em receber e-mails, entendendo que posso cancelar
            facilmente essas comunicações a qualquer momento após a inscrição.{' '}
            <a href="#" className="text-blue-600 hover:underline" data-id="element-34">
              Terms of Use
            </a>
          </label>
        </div>

        <button type="submit" disabled={isSubmitting} className="w-full bg-[#1ABC9C] hover:bg-[#16A085] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold text-lg py-3 px-6 rounded-full shadow-md transition-all transform hover:scale-[1.01] active:scale-[0.99] mt-4 uppercase tracking-wide" data-id="element-35">
          {isSubmitting ? 'ENVIANDO...' : 'CADASTRAR'}
        </button>
      </form>
    </div>;
};
// Form 2 Component (Newsletter with dramatic background)
const Form2 = ({
  onSubmit
}: {
  onSubmit: () => void;
}) => {
  const [agreed, setAgreed] = useState(false);
  const [email, setEmail] = useState('');
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Hidden field values (not displayed but sent on submit)
  const team = 'Brazil';
  const formName = 'SITE OBESCOLHIDOS';
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed) {
      alert('Por favor, aceite os termos para continuar.');
      return;
    }
    setIsSubmitting(true);
    try {
      await callZohoFlow({
        first_name: firstName,
        last_name: lastName,
        email: email,
        dob: birthday,
        team: team,
        formname: formName
      });
      onSubmit();
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Erro ao enviar formulário. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="relative w-full max-w-[550px]" data-id="element-36">
      {/* Background image overlay */}
      <div className="absolute inset-0 -z-10 rounded-2xl overflow-hidden" data-id="element-37">
        <div className="absolute inset-0 bg-black opacity-60" data-id="element-38"></div>
        <img src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&q=80" alt="Background" className="w-full h-full object-cover" data-id="element-39" />
      </div>

      <div className="bg-[#3EACA8] rounded-2xl shadow-xl p-8 md:p-10 relative" data-id="element-40">
        <h2 className="text-white text-xl md:text-2xl font-bold text-center mb-2 leading-tight" data-id="element-41">
          Inscreva-se em nossa newsletter e receba um guia de estudo gratuito.
        </h2>

        <p className="text-white text-xs mb-6 opacity-80" data-id="element-42">
          Click here and add your text
        </p>

        <form className="space-y-5" onSubmit={handleSubmit} data-id="element-43">
          <div className="flex flex-col space-y-2" data-id="element-44">
            <label className="text-white font-medium text-sm" data-id="element-45">
              Seu Melhor E-mail <span className="text-red-400" data-id="element-46">*</span>
            </label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full bg-white border-none rounded px-4 py-3 text-gray-700 focus:ring-2 focus:ring-white focus:outline-none" data-id="element-47" />
          </div>

          <div className="flex flex-col space-y-2" data-id="element-48">
            <label className="text-white font-medium text-sm" data-id="element-49">Last Name</label>
            <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full bg-white border-none rounded px-4 py-3 text-gray-700 focus:ring-2 focus:ring-white focus:outline-none" data-id="element-50" />
          </div>

          <div className="flex flex-col space-y-2" data-id="element-51">
            <label className="text-white font-medium text-sm" data-id="element-52">
              Primeiro Nome
            </label>
            <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full bg-white border-none rounded px-4 py-3 text-gray-700 focus:ring-2 focus:ring-white focus:outline-none" data-id="element-53" />
          </div>

          <div className="flex flex-col space-y-2" data-id="element-54">
            <label className="text-white font-medium text-sm" data-id="element-55">
              Data de Aniversário <span className="text-red-400" data-id="element-56">*</span>
            </label>
            <input type="date" value={birthday} onChange={e => setBirthday(e.target.value)} required className="w-full bg-white border-none rounded px-4 py-3 text-gray-700 focus:ring-2 focus:ring-white focus:outline-none" data-id="element-57" />
          </div>

          <div className="text-red-400 text-xs font-medium pt-2" data-id="element-58">
            *Required Fields
          </div>

          <div className="flex items-start space-x-3 pt-2" data-id="element-59">
            <div className="flex items-center h-5" data-id="element-60">
              <input id="terms2" type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="h-4 w-4 text-white border-white rounded focus:ring-white" data-id="element-61" />
            </div>
            <label htmlFor="terms2" className="text-xs text-white leading-tight" data-id="element-62">
              Li e concordo com os{' '}
              <a href="#" className="underline hover:no-underline" data-id="element-63">
                Termos de Uso
              </a>{' '}
              e{' '}
              <a href="#" className="underline hover:no-underline" data-id="element-64">
                Política de Privacidade
              </a>
              , e autorizo o envio de e-mails, entendendo que posso cancelar
              facilmente essas comunicações a qualquer momento.
            </label>
          </div>

          <button type="submit" disabled={isSubmitting} className="w-full bg-[#2C3E50] hover:bg-[#1A252F] disabled:bg-gray-500 disabled:cursor-not-allowed text-white font-bold text-base py-3 px-6 rounded shadow-md transition-all transform hover:scale-[1.01] active:scale-[0.99] mt-4" data-id="element-65">
            {isSubmitting ? 'ENVIANDO...' : 'Se Inscrever'}
          </button>
        </form>
      </div>
    </div>;
};
// Email Template 1 Component (linked from Form 2)
const EmailTemplate1 = ({
  onConfirm
}: {
  onConfirm: () => void;
}) => {
  return <div className="w-full max-w-[600px] bg-[#f4f4f4] p-4" data-id="element-66">
      <div className="bg-white max-w-[600px] mx-auto" data-id="element-67">
        {/* Header Image */}
        <div className="p-5 text-center" data-id="element-68">
          <img src="https://s12lt.mjt.lu/img2/s12lt/a59a2507-ff04-4610-8966-4a74b3464629/content" alt="The Chosen Brasil" className="w-full max-w-[550px] mx-auto" data-id="element-69" />
        </div>

        {/* Content */}
        <div className="px-6 py-4" data-id="element-70">
          <p className="text-black font-bold text-base mb-3" style={{
          fontFamily: 'Roboto, Arial, Helvetica, sans-serif'
        }} data-id="element-71">
            Obrigado por se cadastrar em nossa newsletter.
          </p>
          <p className="text-black text-base mb-3" style={{
          fontFamily: 'Roboto, Arial, Helvetica, sans-serif'
        }} data-id="element-72">
            Falta pouco para que você tenha acesso a{' '}
            <strong data-id="element-73">todas as novidades do The Chosen no Brasil.</strong>
          </p>
          <p className="text-black text-base mb-6" style={{
          fontFamily: 'Roboto, Arial, Helvetica, sans-serif'
        }} data-id="element-74">
            Mas antes, confirme o seu cadastro clicando no botão abaixo.
          </p>

          {/* CTA Button */}
          <div className="text-center my-6" data-id="element-75">
            <button onClick={onConfirm} className="inline-block bg-[#01b8af] hover:bg-[#019a93] text-white font-bold text-base py-3 px-6 rounded transition-colors cursor-pointer" style={{
            fontFamily: 'Verdana, Helvetica, Arial, sans-serif'
          }} data-id="element-76">
              CONFIRMAR MINHA INSCRIÇÃO
            </button>
          </div>

          {/* Divider */}
          <div className="border-t-2 border-[#e6e6e6] my-6" data-id="element-77"></div>

          {/* Signature */}
          <div className="text-center mb-6" data-id="element-78">
            <p className="text-black font-bold text-base mb-1" style={{
            fontFamily: 'Roboto, Arial, Helvetica, sans-serif'
          }} data-id="element-79">
              Seus amigos,
            </p>
            <p className="text-black font-bold text-base" style={{
            fontFamily: 'Roboto, Arial, Helvetica, sans-serif'
          }} data-id="element-80">
              The Chosen Brasil
            </p>
          </div>

          {/* Social Media Icons */}
          <div className="flex justify-center items-center space-x-2 mb-6" data-id="element-81">
            <a href="https://www.youtube.com/@thechosentvbr" target="_blank" rel="noopener noreferrer" className="w-7 h-7 bg-red-600 rounded flex items-center justify-center hover:opacity-80 transition-opacity" data-id="element-82">
              <span className="text-white text-xs" data-id="element-83">▶</span>
            </a>
            <a href="https://www.instagram.com/thechosentvbr" target="_blank" rel="noopener noreferrer" className="w-7 h-7 bg-[#405de6] rounded flex items-center justify-center hover:opacity-80 transition-opacity" data-id="element-84">
              <span className="text-white text-xs" data-id="element-85">📷</span>
            </a>
            <a href="https://www.tiktok.com/@thechosentvbr" target="_blank" rel="noopener noreferrer" className="w-7 h-7 bg-[#131b20] rounded flex items-center justify-center hover:opacity-80 transition-opacity" data-id="element-86">
              <span className="text-white text-xs" data-id="element-87">♪</span>
            </a>
            <a href="https://www.facebook.com/TheChosenTvBr/" target="_blank" rel="noopener noreferrer" className="w-7 h-7 bg-[#3b5998] rounded flex items-center justify-center hover:opacity-80 transition-opacity" data-id="element-88">
              <span className="text-white text-xs" data-id="element-89">f</span>
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-5 text-center" data-id="element-90">
          <p className="text-[#55575d] text-xs mb-2" style={{
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif'
        }} data-id="element-91">
            Este e-mail foi enviado por: The Chosen Brasil
          </p>
          <p className="text-[#55575d] text-xs mb-3" style={{
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif'
        }} data-id="element-92">
            Não quer mais receber estes e-mails?{' '}
            <a href="#" className="underline hover:no-underline" data-id="element-93">
              Clique aqui
            </a>
          </p>
          <p className="text-[#55575d] text-xs" style={{
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif'
        }} data-id="element-94">
            4 S 2600 W Ste 5, Hurricane, UT 84737-3246 USA
          </p>
        </div>
      </div>
    </div>;
};
// Email Template 2 Component (linked from Form 1)
const EmailTemplate2 = () => {
  return <div className="w-full max-w-[600px] bg-[#f4f4f4] p-4" data-id="element-95">
      <div className="bg-white max-w-[600px] mx-auto" data-id="element-96">
        {/* Header Image */}
        <div className="p-5 text-center" data-id="element-97">
          <img src="https://s12lt.mjt.lu/img2/s12lt/a59a2507-ff04-4610-8966-4a74b3464629/content" alt="The Chosen Brasil" className="w-full max-w-[550px] mx-auto" data-id="element-98" />
        </div>

        {/* Content */}
        <div className="px-6 py-4" data-id="element-99">
          <p className="text-black font-bold text-base mb-3" style={{
          fontFamily: 'Roboto, Arial, Helvetica, sans-serif'
        }} data-id="element-100">
            Obrigado por se cadastrar em nossa newsletter.
          </p>
          <p className="text-black text-base mb-4" style={{
          fontFamily: 'Roboto, Arial, Helvetica, sans-serif'
        }} data-id="element-101">
            Falta pouco para você começar a receber gratuitamente as novidades
            de The Chosen no Brasil, e acessar o seu{' '}
            <strong data-id="element-102">presente exclusivo.</strong>
          </p>
          <p className="text-black text-base mb-6" style={{
          fontFamily: 'Roboto, Arial, Helvetica, sans-serif'
        }} data-id="element-103">
            Mas antes, confirme seu cadastro clicando no botão abaixo para{' '}
            <strong data-id="element-104">receber seu guia de estudo gratuito.</strong>
          </p>

          {/* CTA Button */}
          <div className="text-center my-6" data-id="element-105">
            <a href="https://thechosen.com.br/guiadeestudo-temporada1" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#01b8af] hover:bg-[#019a93] text-white font-bold text-base py-3 px-6 rounded transition-colors" style={{
            fontFamily: 'Verdana, Arial, sans-serif'
          }} data-id="element-106">
              Receber Meu Presente
            </a>
          </div>

          {/* Divider */}
          <div className="border-t-2 border-[#e6e6e6] my-6" data-id="element-107"></div>

          {/* Signature */}
          <div className="text-center mb-6" data-id="element-108">
            <p className="text-black font-bold text-base mb-1" style={{
            fontFamily: 'Roboto, Arial, Helvetica, sans-serif'
          }} data-id="element-109">
              Seus amigos,
            </p>
            <p className="text-black font-bold text-base" style={{
            fontFamily: 'Roboto, Arial, Helvetica, sans-serif'
          }} data-id="element-110">
              The Chosen Brasil
            </p>
          </div>

          {/* Social Media Icons */}
          <div className="flex justify-center items-center space-x-2 mb-6" data-id="element-111">
            <a href="https://www.youtube.com/@thechosentvbr" target="_blank" rel="noopener noreferrer" className="w-7 h-7 bg-red-600 rounded flex items-center justify-center hover:opacity-80 transition-opacity" data-id="element-112">
              <span className="text-white text-xs" data-id="element-113">▶</span>
            </a>
            <a href="https://www.instagram.com/thechosentvbr" target="_blank" rel="noopener noreferrer" className="w-7 h-7 bg-[#405de6] rounded flex items-center justify-center hover:opacity-80 transition-opacity" data-id="element-114">
              <span className="text-white text-xs" data-id="element-115">📷</span>
            </a>
            <a href="https://www.tiktok.com/@thechosentvbr" target="_blank" rel="noopener noreferrer" className="w-7 h-7 bg-[#131b20] rounded flex items-center justify-center hover:opacity-80 transition-opacity" data-id="element-116">
              <span className="text-white text-xs" data-id="element-117">♪</span>
            </a>
            <a href="https://www.facebook.com/TheChosenTvBr/" target="_blank" rel="noopener noreferrer" className="w-7 h-7 bg-[#3b5998] rounded flex items-center justify-center hover:opacity-80 transition-opacity" data-id="element-118">
              <span className="text-white text-xs" data-id="element-119">f</span>
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-5 text-center" data-id="element-120">
          <p className="text-[#55575d] text-xs mb-2" style={{
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif'
        }} data-id="element-121">
            Este e-mail foi enviado por: The Chosen Brasil
          </p>
          <p className="text-[#55575d] text-xs mb-3" style={{
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif'
        }} data-id="element-122">
            Não quer mais receber estes e-mails?{' '}
            <a href="#" className="underline hover:no-underline" data-id="element-123">
              Clique aqui
            </a>
          </p>
          <p className="text-[#55575d] text-xs" style={{
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif'
        }} data-id="element-124">
            4 S 2600 W Ste 5, Hurricane, UT 84737-3246 USA
          </p>
        </div>
      </div>
    </div>;
};
// Landing Page Component
const LandingPage = () => {
  return <div className="w-full max-w-[600px] bg-[#f4f4f4] p-4" data-id="element-125">
      <div className="bg-white max-w-[600px] mx-auto" data-id="element-126">
        {/* Header Image */}
        <div className="p-5 text-center" data-id="element-127">
          <img src="https://s12lt.mjt.lu/img2/s12lt/a59a2507-ff04-4610-8966-4a74b3464629/content" alt="The Chosen Brasil" className="w-full max-w-[550px] mx-auto" data-id="element-128" />
        </div>

        {/* Content */}
        <div className="px-6 py-4" data-id="element-129">
          <p className="text-black text-base text-center mb-3" style={{
          fontFamily: 'Roboto, Arial, Helvetica, sans-serif'
        }} data-id="element-130">
            Olá, Escolhido!
          </p>
          <p className="text-black text-base text-center mb-6" style={{
          fontFamily: 'Roboto, Arial, Helvetica, sans-serif'
        }} data-id="element-131">
            Seu cadastro foi confirmado com sucesso.
          </p>

          {/* Success Image */}
          <div className="text-center my-6" data-id="element-132">
            <img src="https://soh9h.mjt.lu/img2/soh9h/ef955a08-72bf-4dc8-a869-381e0eb06641/content" alt="Success" className="w-full max-w-[250px] mx-auto" data-id="element-133" />
          </div>

          {/* CTA Button */}
          <div className="text-center my-6" data-id="element-134">
            <a href="https://www.osescolhidos.tv" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#01b8af] hover:bg-[#019a93] text-white text-base py-3 px-6 rounded transition-colors" style={{
            fontFamily: 'Roboto, Helvetica, Arial, sans-serif'
          }} data-id="element-135">
              Voltar para OsEscolhidos.tv/Temporada4
            </a>
          </div>

          {/* Divider */}
          <div className="border-t-2 border-[#e6e6e6] my-6" data-id="element-136"></div>

          {/* Signature */}
          <div className="text-center mb-6" data-id="element-137">
            <p className="text-black font-bold text-base mb-1" style={{
            fontFamily: 'Roboto, Arial, Helvetica, sans-serif'
          }} data-id="element-138">
              Seus amigos,
            </p>
            <p className="text-black font-bold text-base" style={{
            fontFamily: 'Roboto, Arial, Helvetica, sans-serif'
          }} data-id="element-139">
              The Chosen Brasil
            </p>
          </div>

          {/* Social Media Icons */}
          <div className="flex justify-center items-center space-x-2 mb-6" data-id="element-140">
            <a href="https://www.youtube.com/@thechosentvbr" target="_blank" rel="noopener noreferrer" className="w-7 h-7 bg-red-600 rounded flex items-center justify-center hover:opacity-80 transition-opacity" data-id="element-141">
              <span className="text-white text-xs" data-id="element-142">▶</span>
            </a>
            <a href="https://www.instagram.com/thechosentvbr" target="_blank" rel="noopener noreferrer" className="w-7 h-7 bg-[#405de6] rounded flex items-center justify-center hover:opacity-80 transition-opacity" data-id="element-143">
              <span className="text-white text-xs" data-id="element-144">📷</span>
            </a>
            <a href="https://www.tiktok.com/@thechosentvbr" target="_blank" rel="noopener noreferrer" className="w-7 h-7 bg-[#131b20] rounded flex items-center justify-center hover:opacity-80 transition-opacity" data-id="element-145">
              <span className="text-white text-xs" data-id="element-146">♪</span>
            </a>
            <a href="https://www.facebook.com/TheChosenTvBr/" target="_blank" rel="noopener noreferrer" className="w-7 h-7 bg-[#3b5998] rounded flex items-center justify-center hover:opacity-80 transition-opacity" data-id="element-147">
              <span className="text-white text-xs" data-id="element-148">f</span>
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-5 text-center" data-id="element-149">
          <p className="text-[#55575d] text-xs mb-2" style={{
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif'
        }} data-id="element-150">
            Este e-mail foi enviado por: The Chosen Brasil
          </p>
          <p className="text-[#55575d] text-xs mb-3" style={{
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif'
        }} data-id="element-151">
            Não quer mais receber estes e-mails?{' '}
            <a href="#" className="underline hover:no-underline" data-id="element-152">
              Clique aqui
            </a>
          </p>
          <p className="text-[#55575d] text-xs" style={{
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif'
        }} data-id="element-153">
            4 S 2600 W Ste 5, Hurricane, UT 84737-3246 USA
          </p>
        </div>
      </div>
    </div>;
};
type ActiveView = 'form1' | 'form2' | 'email1' | 'email2' | 'landing';
export function App() {
  // Check if form parameter exists in URL
  const formParam = getUrlParam('form');
  const hasFormParam = formParam !== null;
  // Initialize activeView based on URL parameter
  const getInitialView = (): ActiveView => {
    if (formParam && FORM_NAME_MAP[formParam]) {
      return FORM_NAME_MAP[formParam];
    }
    return 'form1'; // Default to form1
  };
  const [activeView, setActiveView] = useState<ActiveView>(getInitialView);
  // Also listen for URL changes (e.g., if user navigates with browser back/forward)
  useEffect(() => {
    const handlePopState = () => {
      setActiveView(getInitialView());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  const handleForm1Submit = () => {
    setActiveView('email2');
  };
  const handleForm2Submit = () => {
    setActiveView('email1');
  };
  const handleEmail1Confirm = () => {
    setActiveView('landing');
  };
  return <div className="min-h-screen w-full flex" data-id="element-154">
      {/* Side Navbar with Grouped Sections - Hidden when form param is present */}
      {!hasFormParam && <div className="w-20 bg-gray-900 flex flex-col py-8" data-id="element-155">
          {/* Forms Section */}
          <div className="flex flex-col items-center space-y-3 mb-6" data-id="element-156">
            <span className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold mb-1" data-id="element-157">
              Forms
            </span>
            <button onClick={() => setActiveView('form1')} className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${activeView === 'form1' ? 'bg-[#1ABC9C] text-white shadow-lg' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} title="Form 1 - Guia de Estudo" data-id="element-158">
              <FileText className="w-5 h-5" data-id="element-159" />
            </button>

            <button onClick={() => setActiveView('form2')} className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${activeView === 'form2' ? 'bg-[#3EACA8] text-white shadow-lg' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} title="Form 2 - Newsletter" data-id="element-160">
              <FileText className="w-5 h-5" data-id="element-161" />
            </button>
          </div>

          {/* Divider */}
          <div className="w-12 h-px bg-gray-700 mx-auto mb-6" data-id="element-162"></div>

          {/* Email Templates Section */}
          <div className="flex flex-col items-center space-y-3 mb-6" data-id="element-163">
            <span className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold mb-1" data-id="element-164">
              Emails
            </span>
            <button onClick={() => setActiveView('email1')} className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${activeView === 'email1' ? 'bg-[#01b8af] text-white shadow-lg' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} title="Email 1 - Newsletter Confirmation" data-id="element-165">
              <Mail className="w-5 h-5" data-id="element-166" />
            </button>

            <button onClick={() => setActiveView('email2')} className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${activeView === 'email2' ? 'bg-[#01b8af] text-white shadow-lg' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} title="Email 2 - Study Guide Gift" data-id="element-167">
              <Mail className="w-5 h-5" data-id="element-168" />
            </button>
          </div>

          {/* Divider */}
          <div className="w-12 h-px bg-gray-700 mx-auto mb-6" data-id="element-169"></div>

          {/* Landing Pages Section */}
          <div className="flex flex-col items-center space-y-3" data-id="element-170">
            <span className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold mb-1" data-id="element-171">
              Pages
            </span>
            <button onClick={() => setActiveView('landing')} className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${activeView === 'landing' ? 'bg-[#01b8af] text-white shadow-lg' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} title="Landing Page - Success" data-id="element-172">
              <Home className="w-5 h-5" data-id="element-173" />
            </button>
          </div>
        </div>}

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-auto" data-id="element-174">
        {activeView === 'form1' && <Form1 onSubmit={handleForm1Submit} data-id="element-175" />}
        {activeView === 'form2' && <Form2 onSubmit={handleForm2Submit} data-id="element-176" />}
        {activeView === 'email1' && <EmailTemplate1 onConfirm={handleEmail1Confirm} data-id="element-177" />}
        {activeView === 'email2' && <EmailTemplate2 data-id="element-178" />}
        {activeView === 'landing' && <LandingPage data-id="element-179" />}
      </div>
    </div>;
}